import {
  AriaInteractionMode,
  isSpokenAriaMode,
  showsAriaSubtitles
} from '../../../../../../../pure-js/types/AppAssistant'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { AriaVoicePrimer } from '../../../../../shared/components/AriaVoicePrimer'
import { useRevealExistingAriaSubtitle } from '../../../../../shared/hooks/useRevealExistingAriaSubtitle'
import { resolveCoachStartCopy } from '../coachPageStyles'
import type { useCoachChat } from '../useCoachChat'
import type { useCoachVoiceSession } from '../useCoachVoiceSession'
import { CoachChatPanel } from './CoachChatPanel'
import { CoachContinueOnboardingCard } from './CoachContinueOnboardingCard'
import { CoachEmptyState } from './CoachEmptyState'
import { CoachVoicePanel } from './CoachVoicePanel'

type CoachChat = ReturnType<typeof useCoachChat>

type CoachModeContentProps = {
  mode: AriaInteractionMode
  chat: CoachChat
  session: ReturnType<typeof useCoachVoiceSession>['session']
  isVoiceActive: boolean
  /** No goals yet: the empty state invites goal-setting by name rather than "a conversation". */
  isFirstGoalSetting: boolean
  /** A leadership talk, so the invitation and the primer name that job instead. */
  isLeadershipFocus: boolean
  /** The talk that just ended produced the first goals — offer the way back to onboarding. */
  hasJustCompletedGoals: boolean
  statusLabel: string
  onStartVoice: () => void
  onStopVoice: () => void
  onUseText: () => void
  /** Open the conversation from the empty state — the only thing on this page that may create one. */
  onStartConversation: () => void
  onSend: () => void
  onRetry: (message: Parameters<CoachChat['retryMessage']>[0]) => void
}

export const CoachModeContent = (props: CoachModeContentProps) => {
  const { t } = useTranslation()
  const {
    mode,
    chat,
    session,
    isVoiceActive,
    isFirstGoalSetting,
    isLeadershipFocus,
    hasJustCompletedGoals,
    statusLabel,
    onStartVoice,
    onStopVoice,
    onUseText,
    onStartConversation,
    onSend,
    onRetry
  } = props
  const revealExistingSubtitle = useRevealExistingAriaSubtitle(mode)

  // In a spoken mode the primer is the only start control, so it says what this conversation is for.
  const primerCopy = resolveCoachStartCopy(t, { isFirstGoalSetting, isLeadershipFocus })

  // In a spoken mode the primer is also the retry surface, and the only place that can say what the
  // first conversation is for. Keys stay literal so `yarn check-translations` keeps seeing them.
  const renderConversation = () => {
    if (!isSpokenAriaMode(mode)) {
      return (
        <CoachChatPanel
          messages={chat.messages}
          notice={chat.notice}
          draft={chat.draft}
          isBusy={chat.isBusy}
          isConcluded={Boolean(chat.selectedConversation?.endedAt)}
          onDraftChange={chat.setDraft}
          onSend={onSend}
          onRetry={onRetry}
        />
      )
    }
    if (!isVoiceActive) {
      return (
        <AriaVoicePrimer
          onStartVoice={onStartVoice}
          onUseText={onUseText}
          error={session.error}
          title={primerCopy?.title}
          body={primerCopy?.body}
          startLabel={primerCopy?.cta}
        />
      )
    }
    return (
      <CoachVoicePanel
        statusLabel={statusLabel}
        live={session.isLive}
        muted={session.muted}
        showSubtitles={showsAriaSubtitles(mode)}
        revealExistingSubtitle={revealExistingSubtitle}
        liveTurns={session.turns}
        getInputLevel={session.getInputLevel}
        getOutputLevel={session.getOutputLevel}
        onStop={onStopVoice}
        onToggleMute={session.toggleMute}
      />
    )
  }

  // Only before there is a conversation at all, and only when typing — a spoken mode starts from the
  // primer. Keying this on an empty message list left anyone whose conversation opened without a
  // greeting with no composer and a Start button that returned early.
  if (!isSpokenAriaMode(mode) && !chat.selectedConversation) {
    return (
      <CoachEmptyState
        onStart={onStartConversation}
        disabled={chat.isBusy}
        isFirstGoalSetting={isFirstGoalSetting}
        isLeadershipFocus={isLeadershipFocus}
      />
    )
  }

  // Rendered once, beside whichever surface is showing: goals are saved the moment they are agreed, so
  // this lands mid-conversation in every mode rather than only in the typed view.
  return (
    <>
      {renderConversation()}
      {hasJustCompletedGoals && <CoachContinueOnboardingCard />}
    </>
  )
}
